import React, { useState, useEffect } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Collapse, IconButton, Card, CardContent, Stack, Typography, Menu, MenuItem, Box,
  CircularProgress, Tooltip
} from '@mui/material';
import { styled } from "@mui/material/styles";
import { 
  KeyboardArrowDown, 
  KeyboardArrowUp, 
  SubdirectoryArrowRight, 
  MoreVert
} from '@mui/icons-material';
import { formatDateTime } from 'utils/dateTime';
import { useWebTrader } from 'utils/WebTraderProvider';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrders } from 'redux/order/orderSlice';
import { enqueueSnackbar } from 'notistack';
import { formatEnhancedSymbol } from './utils/symbolFormatter';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  '& > table > tbody > tr > td, & > table > tbody > tr > th': {
    borderBottom: 'unset',
    textAlign: ''
  },
  '& > table > thead > tr > th': {
    textAlign: ''
  }
}));

const StatusDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  display: 'inline-block',
  marginRight: theme.spacing(1)
}));

const OrderRow = ({ order, onDelete, strategyId }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isChild = Boolean(order.parent_order_id);
  const { startOrderModification } = useWebTrader();
  const dispatch = useDispatch();
  
  // Helper function to get enhanced symbol from order data and status
  const getOrderSymbolDisplay = (order) => {
    // Try to get contract descriptions from order status first
    const orderStatus = order.order_status?.[0] || {};
    const symbol = order.data?.symbol || orderStatus.symbol || '';
    
    // Create a trade-like object for the formatter
    const tradeData = {
      symbol: symbol,
      sec_type: order.data?.sec_type || orderStatus.sec_type || 'STK',
      contract_description_1: orderStatus.contract_description_1 || '',
      contract_description_2: orderStatus.contract_description_2 || ''
    };
    
    return formatEnhancedSymbol(tradeData);
  };

  // Helper function to format error message for display
  const getErrorDisplayMessage = (order) => {
    if (!order.has_error) return '';
    
    const errorMessage = order.error_message || 'Unknown error occurred';
    const errorType = order.error?.error_type || '';
    
    // Format common error types for better user understanding
    if (errorType.includes('BrokerAPI') || errorType.includes('API')) {
      return `Broker Error: ${errorMessage}`;
    } else if (errorType.includes('Validation')) {
      return `Validation Error: ${errorMessage}`;
    } else if (errorType.includes('Connection') || errorType.includes('Network')) {
      return `Connection Error: ${errorMessage}`;
    }
    
    return errorMessage;
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCancel = () => {
    // First show notification
    const message = (
      <Box>
        <Typography variant="subtitle2">Order Cancelled</Typography>
        <Typography variant="body2">
          {order.data.side} {order.data.quantity} {order.data.symbol}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>Signal ID: {order.id}</Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>Strategy ID: {strategyId}</Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>{new Date().toLocaleString()}</Typography>
      </Box>
    );
    
    // Then trigger the delete operation
    onDelete(order.id);
    
    // Schedule a delayed refresh to ensure we get updated data
    // Use a longer timeout to make sure backend processing is complete
    setTimeout(() => {
      dispatch(fetchOrders({ strategyId, refresh: true }));
    }, 1000);
    
    enqueueSnackbar(message, { 
      variant: 'success',
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      },
      autoHideDuration: 5000
    });
    handleMenuClose();
  };

  const handleModify = () => {
    startOrderModification(order);
    handleMenuClose();
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          bgcolor: isChild ? 'action.hover' : 'background.paper',
          borderTop: '1px solid rgba(224, 224, 224, 1)'
        }}
      >
        <TableCell padding="checkbox">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{order.id}</TableCell>
        <TableCell>{formatDateTime(order.created)}</TableCell>
        <TableCell>
          {isChild && <SubdirectoryArrowRight fontSize="small" />} {getOrderSymbolDisplay(order)}
        </TableCell>
        <TableCell>{order.data.order_type}</TableCell>
        <TableCell>{order.data.side}</TableCell>
        <TableCell>{order.data.quantity}</TableCell>
        <TableCell>{order.data.average_price || order.data.limit_price || order.data.stop_price || order.data.price||'-'}</TableCell>
        <TableCell>
          {/* Priority: Error > PendingCancel > Active > Inactive */}
          {order.has_error ? (
            <Tooltip title={getErrorDisplayMessage(order)}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusDot sx={{ backgroundColor: 'error.main' }}/>Error
              </Box>
            </Tooltip>
          ) : order.active && order.order_status[0]?.order_status === 'PendingCancel' ? (
            <><StatusDot sx={{ backgroundColor: 'warning.main' }}/>Canceling </>
          ) : order.active ? (
            <><StatusDot />Working </>
          ) : order.order_status[0]?.order_status}
        </TableCell>
        <TableCell padding="none" sx={{ width: '48px' }}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            disabled={order.active === false || order.has_error}
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem 
              onClick={handleModify}
              disabled={!order.parent_order_id && order.order_status[0]?.cannot_cancel_order}
            >
              {order.parent_order_id ? 'Modify Order' : 'Replace Order'}
            </MenuItem>
            <MenuItem 
              onClick={handleCancel}
              disabled={order.order_status[0]?.cannot_cancel_order}
            >
              Cancel Order
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Card variant="outlined" sx={{ m: 1 }}>
              <CardContent sx={{ py: 3 }}>
                <Stack 
                  direction="row" 
                  spacing={4} 
                  flexWrap="wrap" 
                  alignItems="center"
                  useFlexGap 
                  sx={{ '& > *': { flex: '0 0 auto', minWidth: 'auto' }}}
                >
                  {/* Basic Order Info */}
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Security Type:</strong>&nbsp;{(() => {
                      const secType = order.data?.sec_type || order.order_status?.[0]?.sec_type || 'STK';
                      const types = { 'STK': 'Stock', 'OPT': 'Option', 'FUT': 'Future', 'CASH': 'Forex' };
                      return types[secType] || secType;
                    })()}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Time in Force:</strong>&nbsp;{order.data?.tif?.toUpperCase() || '-'}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Filled:</strong>&nbsp;{order.order_status?.[0]?.size_and_fills || `0/${order.data?.quantity || 0}`}
                  </Typography>

                  {/* Exchange & Currency */}
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Exchange:</strong>&nbsp;{order.coin_info?.exchange || order.order_status?.[0]?.listing_exchange || '-'}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Currency:</strong>&nbsp;{order.coin_info?.currency || order.order_status?.[0]?.currency || '-'}
                  </Typography>

                  {/* Company Name */}
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Company:</strong>&nbsp;{order.coin_info?.company_name || order.order_status?.[0]?.company_name || '-'}
                  </Typography>

                  {/* Contract Descriptions */}
                  {order.order_status?.[0]?.contract_description_1 && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <strong>Description:</strong>&nbsp;{order.order_status[0].contract_description_1}
                    </Typography>
                  )}

                  {order.order_status?.[0]?.contract_description_2 && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <strong>Contract Details:</strong>&nbsp;{order.order_status[0].contract_description_2}
                    </Typography>
                  )}

                  {/* Options Specific Details */}
                  {order.data?.sec_type === 'OPT' && (
                    <>
                      {order.data?.strike && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Strike:</strong>&nbsp;${order.data.strike}
                        </Typography>
                      )}
                      {order.data?.expiry && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Expiry:</strong>&nbsp;{order.data.expiry}
                        </Typography>
                      )}
                      {order.data?.right && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          <strong>Type:</strong>&nbsp;{order.data.right === 'C' ? 'Call' : 'Put'}
                        </Typography>
                      )}
                    </>
                  )}

                  {/* Average Fill Price */}
                  {order.order_status?.[0]?.average_price && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <strong>Avg Fill Price:</strong>&nbsp;${parseFloat(order.order_status[0].average_price).toFixed(2)}
                    </Typography>
                  )}

                  {/* Parent/Child Relationship */}
                  {order.parent_order_id && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <strong>Parent Order:</strong>&nbsp;{order.parent_order_id}
                    </Typography>
                  )}

                  {order.source_order_id && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <strong>Copied From:</strong>&nbsp;{order.source_order_id}
                    </Typography>
                  )}
                </Stack>

                {/* Error Message Section - Moved to bottom with better formatting */}
                {order.has_error && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    bgcolor: 'error.lighter',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'error.main'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'error.main',
                        fontWeight: 'medium',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.4
                      }}
                    >
                      <strong>Error:</strong> {getErrorDisplayMessage(order)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const OrdersTable = ({ orders = [], onDeleteOrder, strategyId, isActive }) => {
  const dispatch = useDispatch();
  const ordersLoading = useSelector(state => state.order.ordersLoading);

  // Set up polling only when tab is active
  useEffect(() => {
    if (!isActive) return;

    // Initial fetch with refresh
    dispatch(fetchOrders({ strategyId, refresh: true }));

    // Set up polling interval
    const intervalId = setInterval(() => {
      dispatch(fetchOrders({ strategyId, refresh: true }));
    }, 5000);

    return () => clearInterval(intervalId);
  }, [dispatch, strategyId, isActive]);

  const getOrdersHierarchy = (orders) => {
    const parentOrders = orders.filter(order => !order.parent_order_id);
    
    return parentOrders.reduce((acc, parentOrder) => {
      acc.push(parentOrder);
      
      const childOrders = orders.filter(order => 
        order.parent_order_id === parentOrder.id
      );
      acc.push(...childOrders);
      
      return acc;
    }, []);
  };

  return (
    <StyledTableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small" aria-label="orders table">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Signal ID</TableCell>
            <TableCell sx={{ minWidth: 140 }}>Date/Time</TableCell>
            <TableCell>Symbol</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Side</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Price/Fill</TableCell>
            <TableCell>Status</TableCell>
            <TableCell padding="none" sx={{ width: '48px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {ordersLoading ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Processing order operation...
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : orders.length > 0 ? (
            getOrdersHierarchy(orders).map(order => (
              <OrderRow 
                key={order.id}
                order={order}
                onDelete={(orderId) => onDeleteOrder(orderId)}
                strategyId={strategyId}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={10} align="center">
                No records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default OrdersTable;
